import React, { useLayoutEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { FileText, ScrollText, ShieldCheck, type LucideIcon } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

type LegalDocKey = 'eula' | 'privacy-policy' | 'terms';

interface LegalDocumentLayoutProps {
  /** Which of the three legal surfaces we're on. Controls which cross-links appear in the footer. */
  current: LegalDocKey;
  icon: LucideIcon;
  title: string;
  lastUpdated: string;
  children: React.ReactNode;
}

/**
 * Shared layout for /eula, /privacy-policy, /terms. Matches the polish register
 * used on Support and /legal/accept: icon-badge header, muted "Legal" kicker,
 * tuned prose typography ([&_a] cascade routes inline links to the subsidiary
 * primary), separator + cross-nav footer to the other two docs.
 */
export function LegalDocumentLayout({
  current,
  icon: Icon,
  title,
  lastUpdated,
  children,
}: LegalDocumentLayoutProps) {
  // TanStack Router preserves scroll across SPA route changes. When a user
  // taps an "Also see" cross-link from the bottom of one legal doc, the next
  // doc loads scrolled to the bottom — jarring, especially on mobile.
  // useLayoutEffect (not useEffect) so the reset happens before paint.
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [current]);

  const OTHER_DOCS: {
    key: LegalDocKey;
    to: '/eula' | '/privacy-policy' | '/terms';
    label: string;
    Icon: LucideIcon;
  }[] = [
    { key: 'eula', to: '/eula', label: 'EULA', Icon: FileText },
    { key: 'privacy-policy', to: '/privacy-policy', label: 'Privacy Policy', Icon: ShieldCheck },
    { key: 'terms', to: '/terms', label: 'Terms of Service', Icon: ScrollText },
  ];
  const others = OTHER_DOCS.filter((o) => o.key !== current);

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-2xl px-6 py-12 sm:py-16">
        <header className="mb-10">
          <div className="mb-4 flex items-center gap-3">
            <div
              aria-hidden
              className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary"
            >
              <Icon className="h-5 w-5" />
            </div>
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Legal</span>
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">{title}</h1>
          <p className="mt-2 text-sm text-muted-foreground">Last updated: {lastUpdated}</p>
        </header>

        {/* Prose column. The child-selector cascade below normalizes inline link
            styling across the three (currently-legacy) doc bodies so a
            subsidiary theme swap re-tints links without touching content. */}
        <article
          className={[
            'text-[15px] leading-relaxed text-foreground/90',
            '[&_a]:text-primary [&_a]:underline-offset-2 [&_a:hover]:underline',
            '[&_strong]:text-foreground [&_strong]:font-semibold',
            '[&_ul]:my-4 [&_ul]:ml-6 [&_ul]:list-disc [&_ul]:space-y-2',
            '[&_ol]:my-4 [&_ol]:ml-6 [&_ol]:list-decimal [&_ol]:space-y-2',
          ].join(' ')}
        >
          {children}
        </article>

        <Separator className="mb-5 mt-12" />
        <footer className="flex flex-wrap items-center justify-between gap-4">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">Also see</span>
          <nav
            aria-label="Other legal documents"
            className="flex flex-wrap items-center gap-x-5 gap-y-2"
          >
            {others.map(({ to, label, Icon: LinkIcon }) => (
              <Link
                key={to}
                to={to}
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                <LinkIcon className="h-3.5 w-3.5" aria-hidden />
                {label}
              </Link>
            ))}
          </nav>
        </footer>
      </div>
    </div>
  );
}

/**
 * Section heading used inside a <LegalDocumentLayout> body. Same shape as the
 * per-file Section helpers the three legal components used to define locally;
 * extracted so they can share it (and the tuned typography tokens).
 */
export function LegalSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-8">
      <h2 className="mb-3 text-xl font-semibold tracking-tight text-foreground">{title}</h2>
      {children}
    </section>
  );
}
